import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  connectXero, 
  getXeroStatus, 
  disconnectXero, 
  getAccounts, 
  getTaxRates, 
  getXeroOrganizations,
  activateXeroOrganization,
  updateXeroConfig,
  syncToXero 
} from '../../services/xero';

const XeroSettings = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [config, setConfig] = useState({
    accountCode: '',
    taxRateCode: '',
    bankAccountCode: ''
  });
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    loadStatus();
    
    // Check if we have an OAuth code (Xero redirected here instead of backend)
    const code = searchParams.get('code');
    if (code) {
      // Forward the code to backend callback endpoint
      handleOAuthCallback(code);
      return;
    }
    
    // Check for OAuth callback success/error
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    if (connected === 'true') {
      setSuccessMessage('Xero connected successfully!');
      // Clear the query params from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      // Reload status to show connection
      loadStatus();
    }
    if (error === 'true') {
      const errorMessage = searchParams.get('message') || 'Failed to connect Xero';
      setError(errorMessage);
      // Clear the query params from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);

  const handleOAuthCallback = async (code) => {
    try {
      setConnecting(true);
      setError(null);
      setSuccessMessage('Processing Xero connection...');
      
      // Get the full callback URL to forward to backend
      const currentUrl = window.location.href;
      const urlParts = new URL(currentUrl);
      
      // Construct backend callback URL
      // If frontend and backend are on same domain, use relative path
      // Otherwise, use the backend API base URL
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const backendCallbackUrl = code 
        ? `${apiBaseUrl}/api/admin/xero/callback?code=${code}&scope=${searchParams.get('scope') || ''}`
        : `${urlParts.origin}/api/admin/xero/callback${urlParts.search}`;
      
      // Redirect to backend callback endpoint
      window.location.href = backendCallbackUrl;
    } catch (err) {
      setError('Failed to process OAuth callback: ' + err.message);
      setConnecting(false);
    }
  };

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getXeroStatus();
      
      if (response.success && response.data) {
        setStatus(response.data);
        setSelectedTenantId(response.data.tenantId || '');
        if (response.data.connected) {
          setConfig({
            accountCode: response.data.accountCode || '',
            taxRateCode: response.data.taxRateCode || '',
            bankAccountCode: response.data.bankAccountCode || ''
          });
          await loadOrganizations();
          await loadAccounts();
          await loadTaxRates();
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load Xero status');
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await getXeroOrganizations();
      if (response.success && Array.isArray(response.data)) {
        setOrganizations(response.data);
        const active = response.data.find((o) => o.isActive);
        setSelectedTenantId(active?.tenantId || response.data[0]?.tenantId || '');
      }
    } catch (err) {
      console.error('Failed to load Xero organisations:', err);
      setOrganizations([]);
    }
  };

  const handleActivateTenant = async () => {
    try {
      setError(null);
      await activateXeroOrganization({ tenantId: selectedTenantId });
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Failed to activate organization');
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await getAccounts();
      if (response.success && response.data) {
        setAccounts(response.data);
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
    }
  };

  const loadTaxRates = async () => {
    try {
      const response = await getTaxRates();
      if (response.success && response.data) {
        setTaxRates(response.data);
      }
    } catch (err) {
      console.error('Failed to load tax rates:', err);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      const response = await connectXero();
      
      if (response.success && response.data?.authUrl) {
        // Redirect to Xero authorization page
        window.location.href = response.data.authUrl;
      } else {
        setError('Failed to get authorization URL');
      }
    } catch (err) {
      setError(err.message || 'Failed to connect Xero');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Xero? This will stop automatic invoice generation and payment syncing.')) {
      return;
    }

    try {
      setError(null);
      await disconnectXero();
      setStatus({ connected: false });
      setSuccessMessage('Xero disconnected successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to disconnect Xero');
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      await updateXeroConfig(config);
      setSuccessMessage('Configuration saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const response = await syncToXero();
      setSuccessMessage(response.message || 'Sync completed successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Failed to sync');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1200px] mx-auto space-y-6 pb-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-400 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto space-y-6 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Xero Integration</h1>
        <p className="text-sm text-gray-500">Connect your Xero account to enable automatic invoice generation and payment syncing</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Connection Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Connection Status</h3>
            <p className="text-sm text-gray-500 mt-1">
              {status?.connected 
                ? `Connected to ${status.organizationName || 'Xero'}`
                : 'Not connected to Xero'
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            {status?.connected ? (
              <>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  status.isValid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {status.isValid ? 'Active' : 'Expired'}
                </span>
                <button
                  onClick={handleDisconnect}
                  className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={handleConnect}
                disabled={connecting}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500 disabled:opacity-50"
              >
                {connecting ? 'Connecting...' : 'Connect Xero'}
              </button>
            )}
          </div>
        </div>

        {status?.connected && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Connected At:</span>
                <span className="ml-2 text-gray-900">
                  {status.connectedAt ? new Date(status.connectedAt).toLocaleString() : 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Last Synced:</span>
                <span className="ml-2 text-gray-900">
                  {status.lastSyncedAt ? new Date(status.lastSyncedAt).toLocaleString() : 'Never'}
                </span>
              </div>
            </div>
            <div className="mt-4">
              <button
                onClick={handleSync}
                disabled={syncing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                {syncing ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Configuration */}
      {status?.connected && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h3>

            {organizations.length > 1 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Xero Organisation
                </label>
                <select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  {organizations.map((org) => (
                    <option key={org.tenantId} value={org.tenantId}>
                      {org.organizationName || org.tenantId}
                      {org.isActive ? ' (Active)' : ''}
                    </option>
                  ))}
                </select>

                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    onClick={handleActivateTenant}
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500"
                  >
                    Use selected organisation
                  </button>
                </div>
              </div>
            )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Revenue Account Code
              </label>
              <select
                value={config.accountCode}
                onChange={(e) => setConfig({ ...config, accountCode: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                <option value="">Select Account</option>
                {accounts.map(account => (
                  <option key={account.code} value={account.code}>
                    {account.code} - {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Tax Rate
              </label>
              <select
                value={config.taxRateCode}
                onChange={(e) => setConfig({ ...config, taxRateCode: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                <option value="">Select Tax Rate</option>
                {taxRates.map(rate => (
                  <option key={rate.taxType} value={rate.taxType}>
                    {rate.name || rate.taxType}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bank Account Code (for payments)
              </label>
              <select
                value={config.bankAccountCode || ''}
                onChange={(e) => setConfig({ ...config, bankAccountCode: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                <option value="">Select Bank Account</option>
                {accounts.filter(acc => acc.type === 'BANK').map(account => (
                  <option key={account.code} value={account.code}>
                    {account.code} - {account.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Required so that when customers pay via Stripe, the payment is recorded in Xero and the invoice status updates to Paid.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                className="px-6 py-2 text-sm font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default XeroSettings;

